import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const ContactSupport = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    name: profile?.full_name || '',
    email: profile?.email || '',
    category: '',
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categoryOptions = [
    { value: '', label: 'Select a category' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'account', label: 'Account & Billing' },
    { value: 'verification', label: 'Document Verification' },
    { value: 'recommendations', label: 'AI Recommendations' },
    { value: 'guidelines', label: 'iRefer Guidelines' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low - General inquiry' },
    { value: 'normal', label: 'Normal - Standard support' },
    { value: 'high', label: 'High - Service affecting' },
    { value: 'urgent', label: 'Urgent - System down' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1500);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation 
          userRole={profile?.role || 'clinician'} 
          creditBalance={profile?.credits_remaining || 0}
          subscriptionTier={profile?.subscription_tier || 'Free'} 
        />

        <div className="main-content">
          <div className="max-w-2xl mx-auto">
            <div className="clinical-card p-12 text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Support Request Submitted!
              </h2>
              <p className="text-muted-foreground mb-6">
                Thank you for contacting us. We've received your support request and will respond within 24 hours.
                A confirmation has been sent to <span className="font-medium text-foreground">{formData?.email}</span>
              </p>
              <div className="space-y-3">
                <Button 
                  variant="default" 
                  fullWidth
                  iconName="Home"
                  onClick={() => navigate('/dashboard')}
                >
                  Return to Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  fullWidth
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      name: profile?.full_name || '',
                      email: profile?.email || '',
                      category: '',
                      subject: '',
                      message: '',
                      priority: 'normal'
                    });
                  }}
                >
                  Submit Another Request
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation 
        userRole={profile?.role || 'clinician'} 
        creditBalance={profile?.credits_remaining || 0}
        subscriptionTier={profile?.subscription_tier || 'Free'} 
      />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Contact Support</h1>
                <p className="text-muted-foreground">
                  Get help from our support team. We typically respond within 24 hours.
                </p>
              </div>
              <Button 
                variant="outline" 
                iconName="ArrowLeft"
                onClick={() => navigate('/dashboard')}
              >
                Back
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Support Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="clinical-card p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Your Name *"
                    value={formData?.name}
                    onChange={(e) => handleChange('name', e?.target?.value)}
                    placeholder="Enter your full name"
                    required
                  />
                  <Input
                    label="Email Address *"
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleChange('email', e?.target?.value)}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <Select
                  label="Issue Category *"
                  options={categoryOptions}
                  value={formData?.category}
                  onChange={(e) => handleChange('category', e?.target?.value)}
                  required
                />

                <Input
                  label="Subject *"
                  value={formData?.subject}
                  onChange={(e) => handleChange('subject', e?.target?.value)}
                  placeholder="Brief description of your issue"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Message *
                  </label>
                  <textarea
                    value={formData?.message}
                    onChange={(e) => handleChange('message', e?.target?.value)}
                    placeholder="Describe your issue in detail. Include any error messages, screenshots, or steps to reproduce the problem."
                    rows={6}
                    required
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background text-foreground"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Minimum 50 characters. Be as detailed as possible for faster resolution.
                  </p>
                </div>

                <Select
                  label="Priority"
                  options={priorityOptions}
                  value={formData?.priority}
                  onChange={(e) => handleChange('priority', e?.target?.value)}
                />

                <div className="clinical-card p-4 bg-muted/50">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">Response Time Expectations:</p>
                      <ul className="space-y-1 ml-4 list-disc">
                        <li>Urgent: Within 2-4 hours</li>
                        <li>High: Within 8 hours</li>
                        <li>Normal: Within 24 hours</li>
                        <li>Low: Within 48 hours</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    loading={submitting}
                    iconName="Send"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </div>
              </form>
            </div>

            {/* Quick Help Sidebar */}
            <div className="space-y-6">
              <div className="clinical-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="BookOpen" size={20} color="var(--color-primary)" />
                  <h3 className="font-semibold text-foreground">Quick Help</h3>
                </div>
                <div className="space-y-3">
                  <Button
                    variant="ghost"
                    fullWidth
                    className="justify-start"
                    iconName="BookOpen"
                    onClick={() => navigate('/user-guide')}
                  >
                    User Guide
                  </Button>
                  <Button
                    variant="ghost"
                    fullWidth
                    className="justify-start"
                    iconName="Video"
                  >
                    Video Tutorials
                  </Button>
                  <Button
                    variant="ghost"
                    fullWidth
                    className="justify-start"
                    iconName="HelpCircle"
                  >
                    FAQ
                  </Button>
                </div>
              </div>

              <div className="clinical-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Mail" size={20} color="var(--color-primary)" />
                  <h3 className="font-semibold text-foreground">Direct Contact</h3>
                </div>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Icon name="Mail" size={16} className="mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">Email</p>
                      <a href="mailto:care@mydocrefer.org" className="text-primary hover:underline">
                        care@mydocrefer.org
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icon name="Clock" size={16} className="mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">Support Hours</p>
                      <p>24/7 for urgent issues</p>
                      <p>Mon-Fri 9AM-6PM for general support</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="clinical-card p-6 bg-primary/10 border border-primary/20">
                <div className="flex items-start gap-2 mb-3">
                  <Icon name="Zap" size={20} color="var(--color-primary)" />
                  <h4 className="font-semibold text-foreground">Priority Support</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Professional and Enterprise plan members receive priority support with faster response times.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => navigate('/account-settings')}
                >
                  Upgrade Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSupport;